import { Box, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { usePagesTranslation } from "../../../../pages/locales";
import SlidingPlayMain from "../engine/SlidingPlayMain";
import { pickSlidingOriginalImageUrl } from "../../../../pages/browse-sliding/slidingBrowseUtils";
import { resolveSlidingBoardPreview } from "../lib/api";
import {
    isDifficultyLocked,
    isGridSizeLocked,
} from "../lib/subscriptionManager";
import { useSubscription } from "../lib/SubscriptionContext";
import HomePlayButton from "./HomePlayButton";
import LibraryCardConfigPills from "./LibraryCardConfigPills";
import PuzzlePublishedAtLine from "./PuzzlePublishedAtLine";
import { gridSizeAvailable, levelAvailable } from "./WirePlayControls";
import { useSlidingAppearance } from "../lib/AppearanceContext";
import { slidingPlayGridProps } from "../lib/appearance";
import { getHomePremium, homePremiumGlassCardSx } from "../styles/homePremiumStyles";
import HomeLightBlueMist from "./HomeLightBlueMist";
import { CHALLENGE_GRID, DIFFICULTIES, DIFFICULTY_LABELS, QUICK_PLAY_GRID } from "../routes";

const GRID_OPTIONS = [
    { size: QUICK_PLAY_GRID, label: "4×4" },
    { size: CHALLENGE_GRID, label: "6×6" },
];

const systemFont =
    '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif';
const displayFont =
    '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif';
const tabletLandscape = "@media (min-width: 900px) and (orientation: landscape)";

/**
 * Home dashboard: featured puzzle card (play-page grid styling).
 */
export default function ChooseGridPanel({
    storyTitle,
    storyGroup,
    gridSize,
    difficulty,
    onGridChange,
    onDifficultyChange,
    onStart,
    starting,
    disabled,
    loading,
}) {
    const { t } = usePagesTranslation();
    const { isSubscribed } = useSubscription();
    const { tokens, isDark } = useSlidingAppearance();
    const home = getHomePremium(isDark);
    // While the featured puzzle is still loading, don't flash the hardcoded fallback title.
    const title =
        storyTitle?.trim() ||
        (loading
            ? ""
            : t("native.home.featuredPuzzleFallback", {
                  defaultValue: "The Morning Confluence",
              }));

    const boardPreview = useMemo(
        () => resolveSlidingBoardPreview(storyGroup, gridSize, difficulty),
        [storyGroup, gridSize, difficulty],
    );

    const fallbackImageUrl = useMemo(() => {
        const url = storyGroup?.story ? pickSlidingOriginalImageUrl(storyGroup.story) : null;
        return typeof url === "string" && url.trim() ? url.trim() : null;
    }, [storyGroup]);

    const templates = useMemo(
        () => (Array.isArray(storyGroup?.templates) ? storyGroup.templates : []),
        [storyGroup],
    );

    const gridPillOptions = useMemo(
        () =>
            GRID_OPTIONS.map((opt) => {
                const available =
                    templates.length === 0 || gridSizeAvailable(templates, opt.size);
                const locked = isGridSizeLocked(opt.size, isSubscribed);
                return {
                    ...opt,
                    locked,
                    disabled: !available,
                };
            }),
        [templates, isSubscribed],
    );

    const levelPillOptions = useMemo(
        () =>
            DIFFICULTIES.map((d) => {
                const available =
                    templates.length === 0 || levelAvailable(templates, gridSize, d);
                const locked = isDifficultyLocked(gridSize, d, isSubscribed);
                return {
                    value: d,
                    label: DIFFICULTY_LABELS[d],
                    locked,
                    disabled: !available,
                };
            }),
        [templates, gridSize, isSubscribed],
    );

    const previewBoardSx = {
        // Prefer container queries when the flex parent has a real size (iPad).
        // Android WebView often reports support for cqi/cqh but resolves them to 0 in
        // nested flex + container-type:size layouts — floor with vh so the board stays visible.
        width: "min(100%, max(36vh, min(100cqi, 100cqh)))",
        maxWidth: "100%",
        maxHeight: "100%",
        aspectRatio: "1 / 1",
        overflow: "hidden",
        borderRadius: "12px",
        flexShrink: 0,
        pointerEvents: "none",
        bgcolor: tokens.imagePlaceholderBg,
        [tabletLandscape]: {
            width: "min(100cqi, 100cqh)",
        },
        "@supports not (width: 1cqi)": {
            width: {
                xs: "min(100%, 42vh)",
                sm: "min(100%, 52vh)",
                md: "min(100%, 62vh, 720px)",
            },
            height: "auto",
        },
    };

    return (
        <Box
            sx={{
                ...homePremiumGlassCardSx(isDark),
                position: "relative",
                zIndex: 1,
                width: "100%",
                boxSizing: "border-box",
                borderRadius: "18px",
                overflow: "hidden",
                p: { xs: 1.75, sm: 2, md: 2.25 },
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                isolation: "isolate",
                [tabletLandscape]: {
                    p: 1.5,
                },
            }}
            component="section"
            aria-label="Choose your grid"
        >
            <HomeLightBlueMist isDark={isDark} />

            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 0,
                    [tabletLandscape]: {
                        display: "grid",
                        gridTemplateAreas: `
                            "title"
                            "published"
                            "board"
                            "controls"
                        `,
                        gridTemplateColumns: "minmax(0, 1fr)",
                        gridTemplateRows: "auto auto minmax(0, 1fr) auto",
                    },
                }}
            >
            <Typography
                component="h2"
                sx={{
                    fontFamily: displayFont,
                    fontSize: "1.125rem",
                    fontWeight: 700,
                    color: home.text,
                    lineHeight: 1.3,
                    letterSpacing: "-0.01em",
                    mb: 0.35,
                    flexShrink: 0,
                    wordBreak: "break-word",
                    [tabletLandscape]: {
                        gridArea: "title",
                        alignSelf: "end",
                        fontSize: "clamp(1.25rem, 1.6vw, 1.75rem)",
                        mt: 1,
                    },
                }}
            >
                {title || "\u00A0"}
            </Typography>

            <PuzzlePublishedAtLine
                group={storyGroup}
                sx={{
                    mb: 1,
                    textAlign: "left",
                    color: home.textMuted,
                    fontSize: "0.75rem",
                    fontFamily: systemFont,
                    flexShrink: 0,
                    [tabletLandscape]: {
                        gridArea: "published",
                        mb: 0,
                    },
                }}
            />

            {boardPreview || fallbackImageUrl ? (
                <Box
                    sx={{
                        flex: 1,
                        minHeight: "36vh",
                        width: "100%",
                        mb: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        containerType: "size",
                        [tabletLandscape]: {
                            gridArea: "board",
                            minHeight: 0,
                            height: "100%",
                            mb: 0,
                            alignItems: "center",
                            justifyContent: "center",
                        },
                    }}
                >
                    {boardPreview ? (
                        <Box sx={previewBoardSx}>
                            <SlidingPlayMain
                                key={`${boardPreview.gridSize}-${difficulty}-${boardPreview.startingLayout.join("-")}`}
                                imageUrl={boardPreview.imageUrl}
                                gridSize={boardPreview.gridSize}
                                startingLayout={boardPreview.startingLayout}
                                hideCompletedCaption
                                animateMoves={false}
                                playMoveSound={false}
                                fillParent
                                {...slidingPlayGridProps(tokens)}
                            />
                        </Box>
                    ) : (
                        <Box sx={previewBoardSx}>
                            <Box
                                component="img"
                                src={fallbackImageUrl}
                                alt=""
                                sx={{
                                    display: "block",
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                }}
                            />
                        </Box>
                    )}
                </Box>
            ) : null}

            <Box
                sx={{
                    flexShrink: 0,
                    mt: boardPreview || fallbackImageUrl ? 0 : "auto",
                    [tabletLandscape]: {
                        gridArea: "controls",
                        alignSelf: "end",
                        width: "100%",
                        mb: 1,
                    },
                }}
            >
                <LibraryCardConfigPills
                    variant="premium"
                    gridOptions={gridPillOptions}
                    levelOptions={levelPillOptions}
                    gridSize={gridSize}
                    difficulty={difficulty}
                    onGridChange={onGridChange}
                    onDifficultyChange={onDifficultyChange}
                    disabled={disabled || starting}
                />

                <HomePlayButton
                    onClick={onStart}
                    disabled={disabled}
                    loading={starting}
                    ariaLabel={t("native.home.solvePuzzle", {
                        defaultValue: "Solve Puzzle",
                    })}
                    label={t("native.home.solvePuzzle", { defaultValue: "Solve Puzzle" })}
                />
            </Box>
            </Box>
        </Box>
    );
}
